import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [isHttps,  setIsHttps]  = useState(false);
  const [caExists, setCaExists] = useState(false);

  // Detect HTTPS and whether CA cert is available for download
  // Only show the banner if we're on an untrusted/internal cert
  useEffect(() => {
    const isHttpsProto = window.location.protocol === 'https:';
    setIsHttps(isHttpsProto);
    if (isHttpsProto) {
      // Check if cert is trusted by trying a fetch — if no error, cert is trusted
      // Only show CA download banner if we're on an internal/self-signed cert
      fetch('/api/auth/ping', { method: 'GET' })
        .then(() => setCaExists(false))  // trusted cert — no banner needed
        .catch(() => {
          // Connection error — check if CA cert exists for download
          fetch('/ca.crt', { method: 'HEAD' })
            .then(r => { if (r.ok) setCaExists(true); })
            .catch(() => {});
        });
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/login', { username, password });
      const { token, user } = res.data;
      localStorage.setItem('adminToken', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      onLogin(token, user);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  }

  const caUrl = `${window.location.protocol}//${window.location.host}/ca.crt`;

  return (
    <div className="login-wrap">
      <div className="login-box">

        {/* ── "Not Secure" fix banner — only shown on HTTPS with untrusted cert ── */}
        {isHttps && caExists && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
            padding: '10px 14px', marginBottom: 18, fontSize: 11, color: '#92400e',
            lineHeight: 1.6
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              🔒 Browser shows "Not Secure"?
            </div>
            <div style={{ marginBottom: 8 }}>
              Install the PatchSphere CA certificate once to remove this warning on all browsers on this device.
            </div>
            <a
              href={caUrl}
              download="PatchSphere-CA.crt"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: '#0f2d6b', color: 'white', borderRadius: 6,
                padding: '5px 12px', fontSize: 11, fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              ⬇ Download CA Certificate
            </a>
            <div style={{ marginTop: 8, fontSize: 10, color: '#b45309' }}>
              <strong>Windows:</strong> Double-click → Install → "Trusted Root Certification Authorities"<br />
              <strong>Android:</strong> Settings → Security → Install certificate<br />
              <strong>iOS/Mac:</strong> Tap file → Settings → Profile → Install → Trust
            </div>
          </div>
        )}

        {/* ── Logo ── */}
        <div className="login-logo" style={{ alignItems: 'center', gap: 16, marginBottom: 28 }}>
          <img
            src="/patchsphere-logo.png"
            alt="PatchSphere AI"
            style={{ width: '100%', maxWidth: 280, height: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        </div>

        {/* ── Heading ── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1e2a3b', marginBottom: 4 }}>
            Sign in to your account
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            Enter your username and password to continue
          </div>
        </div>

        {/* ── Form ── */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              className="form-input"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="alert error" style={{ marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 14 }}
            disabled={loading}
          >
            {loading ? '⟳ Signing in...' : '→ Sign In'}
          </button>
        </form>

        {/* ── Footer ── */}
        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 10, color: '#1e2a3b', lineHeight: '1.6' }}>
          Centralized Endpoint Governance, Asset Management,<br />
          Vulnerability Remediation, and Patch Automation
        </div>
      </div>
    </div>
  );
}
